import { RouteMetadata } from './types';

const CUSTOMER_ORG_NOT_CHAT_PARTITION_HINT =
    'This is a customer organization under admin review, not the chat partition';

const DASHBOARD_TABS_HINT = 'Dashboard tabs are under /admin/dashboard';

const PLATFORM_COST_HINT = 'Platform cost data is global-scope admin data';
const SERVICES_SESSION_MOCK_HINT =
    'Services catalog is session mock data; changes reset on refresh and nothing calls Stripe';

export const adminMetadata: RouteMetadata[] = [
    {
        pattern: '/admin/system-agents/:systemAgentId/history',
        section: 'admin',
        view: 'system-agent-history',
        descriptionTemplate:
            'User is viewing platform system agent {systemAgentId} execution history in admin',
        capabilities: [
            'View historical runs for the selected platform system agent',
            'Filter runs by execution status',
            'Open run details and inspect logs',
            'Investigate failed platform agent executions',
        ],
        hints: [
            'Platform agent history uses the agent-prefixed workflow ID under the platform partition',
            'Status filters come from URL search parameters',
            'Runs here are never attributed to a customer organization',
        ],
    },
    {
        pattern: '/admin/system-agents/:systemAgentId/stats',
        section: 'admin',
        view: 'system-agent-stats',
        descriptionTemplate:
            'User is viewing platform system agent {systemAgentId} performance stats in admin',
        capabilities: [
            'View aggregate runs, success rate, latency, tokens, and cost',
            'Analyze daily trends for platform agent runs and threads',
            'Adjust the date range for analytics',
        ],
        hints: [
            'Stats queries use the reserved platform partition key',
            'Date range selection is controlled by the daysBack search parameter',
        ],
    },
    {
        pattern: '/admin/system-agents/:systemAgentId/errors',
        section: 'admin',
        view: 'system-agent-errors',
        descriptionTemplate:
            'User is viewing platform system agent {systemAgentId} errors in admin',
        capabilities: [
            'Browse recent errors for the selected platform system agent',
            'Open failing runs for investigation',
        ],
        hints: ['Errors are scoped to the platform partition, not a customer organization'],
    },
    {
        pattern: '/admin/system-agents/:systemAgentId',
        section: 'admin',
        view: 'system-agent-overview',
        descriptionTemplate:
            'User is viewing the overview for platform system agent {systemAgentId} in admin',
        capabilities: [
            'Inspect platform system agent metadata and defaults',
            'Review configured tools and toolboxes',
            'Navigate to history, stats, and errors tabs',
        ],
        hints: [
            'Platform agents have no Access tab (no member allowlist)',
            'Chat with this agent from the admin shell under the platform partition',
        ],
    },
    {
        pattern: '/admin/system-agents',
        section: 'admin',
        view: 'system-agents-list',
        descriptionTemplate: 'User is browsing platform system agents in admin',
        capabilities: [
            'Browse platform-tenancy system agents',
            'Open a platform system agent details view',
        ],
        hints: [
            'Only agents with tenancy platform appear here',
            'Organization-scoped agents are listed under the org System Agents page',
        ],
    },
    {
        pattern: '/admin/organizations/:organizationId/services/:instanceId/billing',
        section: 'admin',
        view: 'organization-service-instance-billing',
        descriptionTemplate:
            'User is viewing billing for organization service {instanceId} in {organizationId}',
        capabilities: ['Inspect and edit organization service billing'],
        hints: [CUSTOMER_ORG_NOT_CHAT_PARTITION_HINT],
    },
    {
        pattern: '/admin/organizations/:organizationId/services/:instanceId/requirements',
        section: 'admin',
        view: 'organization-service-instance-requirements',
        descriptionTemplate:
            'User is viewing requirements for organization service {instanceId} in {organizationId}',
        capabilities: ['Write organization service requirements'],
        hints: [CUSTOMER_ORG_NOT_CHAT_PARTITION_HINT],
    },
    {
        pattern: '/admin/organizations/:organizationId/services/:instanceId/attachments',
        section: 'admin',
        view: 'organization-service-instance-attachments',
        descriptionTemplate:
            'User is viewing attachments for organization service {instanceId} in {organizationId}',
        capabilities: ['Attach evidence files to an organization service'],
        hints: [CUSTOMER_ORG_NOT_CHAT_PARTITION_HINT],
    },
    {
        pattern: '/admin/organizations/:organizationId/services/:instanceId',
        section: 'admin',
        view: 'organization-service-instance',
        descriptionTemplate:
            'User is viewing organization service {instanceId} in {organizationId}',
        capabilities: ['Inspect an organization service instance'],
        hints: [CUSTOMER_ORG_NOT_CHAT_PARTITION_HINT],
    },
    {
        pattern: '/admin/organizations/:organizationId/services',
        section: 'admin',
        view: 'organization-services',
        descriptionTemplate: 'User is viewing services for admin organization {organizationId}',
        capabilities: ['Inspect organization service instances', 'Attach catalog services'],
        hints: [CUSTOMER_ORG_NOT_CHAT_PARTITION_HINT],
    },
    {
        pattern: '/admin/organizations/:organizationId/subscription',
        section: 'admin',
        view: 'organization-subscription',
        descriptionTemplate: 'User is viewing subscription for admin organization {organizationId}',
        capabilities: ['Inspect organization subscription status and billing'],
        hints: [CUSTOMER_ORG_NOT_CHAT_PARTITION_HINT],
    },
    {
        pattern: '/admin/organizations/:organizationId/costs',
        section: 'admin',
        view: 'organization-costs',
        descriptionTemplate: 'User is viewing costs for admin organization {organizationId}',
        capabilities: ['Inspect organization cost attribution'],
        hints: [CUSTOMER_ORG_NOT_CHAT_PARTITION_HINT],
    },
    {
        pattern: '/admin/organizations/:organizationId/workflows',
        section: 'admin',
        view: 'organization-workflows',
        descriptionTemplate: 'User is viewing workflows for admin organization {organizationId}',
        capabilities: ['Inspect organization workflow metrics'],
        hints: [CUSTOMER_ORG_NOT_CHAT_PARTITION_HINT],
    },
    {
        pattern: '/admin/organizations/:organizationId',
        section: 'admin',
        view: 'organization-detail',
        descriptionTemplate: 'User is viewing admin organization {organizationId}',
        capabilities: [
            'Inspect organization services, subscription, costs, and workflows',
            'Navigate organization admin tabs',
        ],
        hints: [CUSTOMER_ORG_NOT_CHAT_PARTITION_HINT],
    },
    {
        pattern: '/admin/organizations',
        section: 'admin',
        view: 'organizations',
        descriptionTemplate: 'User is browsing organizations in admin',
        capabilities: ['Browse customer organizations', 'Open an organization detail view'],
        hints: ['Organization list is admin-scoped'],
    },
    {
        pattern: '/admin/platform-costs/:providerId',
        section: 'admin',
        view: 'platform-costs-provider',
        descriptionTemplate: 'User is viewing platform cost provider {providerId} in admin',
        capabilities: ['Inspect a platform cost provider'],
        hints: [PLATFORM_COST_HINT],
    },
    {
        pattern: '/admin/platform-costs',
        section: 'admin',
        view: 'platform-costs',
        descriptionTemplate: 'User is viewing platform costs in admin',
        capabilities: ['Inspect platform cost overview and providers'],
        hints: [PLATFORM_COST_HINT],
    },
    {
        pattern: '/admin/dashboard/summary',
        section: 'admin',
        view: 'dashboard-summary',
        descriptionTemplate: 'User is viewing the admin dashboard summary tab',
        capabilities: ['Review platform business insights summary'],
        hints: [DASHBOARD_TABS_HINT],
    },
    {
        pattern: '/admin/dashboard/growth',
        section: 'admin',
        view: 'dashboard-growth',
        descriptionTemplate: 'User is viewing the admin dashboard growth tab',
        capabilities: ['Review platform growth insights'],
        hints: [DASHBOARD_TABS_HINT],
    },
    {
        pattern: '/admin/dashboard/unit-economics',
        section: 'admin',
        view: 'dashboard-unit-economics',
        descriptionTemplate: 'User is viewing the admin dashboard unit economics tab',
        capabilities: ['Review platform unit economics'],
        hints: [DASHBOARD_TABS_HINT],
    },
    {
        pattern: '/admin/dashboard/retention',
        section: 'admin',
        view: 'dashboard-retention',
        descriptionTemplate: 'User is viewing the admin dashboard retention tab',
        capabilities: ['Review platform retention insights'],
        hints: [DASHBOARD_TABS_HINT],
    },
    {
        pattern: '/admin/dashboard/engagement',
        section: 'admin',
        view: 'dashboard-engagement',
        descriptionTemplate: 'User is viewing the admin dashboard engagement tab',
        capabilities: ['Review platform engagement insights'],
        hints: [DASHBOARD_TABS_HINT],
    },
    {
        pattern: '/admin/dashboard/organizations',
        section: 'admin',
        view: 'dashboard-organizations',
        descriptionTemplate: 'User is viewing the admin dashboard organizations tab',
        capabilities: ['Review organizations on the business insights dashboard'],
        hints: [DASHBOARD_TABS_HINT],
    },
    {
        pattern: '/admin/dashboard',
        section: 'admin',
        view: 'dashboard',
        descriptionTemplate: 'User is viewing the admin business insights dashboard',
        capabilities: ['Review platform business insights tabs'],
        hints: [DASHBOARD_TABS_HINT],
    },
    {
        pattern: '/admin/workflow-ops',
        section: 'admin',
        view: 'workflow-ops',
        descriptionTemplate: 'User is viewing system-wide workflow ops fleet health in admin',
        capabilities: [
            'Inspect cross-organization workflow execution volume and success rate',
            'Compare top organizations and failing workflows over a selected window',
        ],
        hints: [
            'Workflow Ops is under Overview beside Dashboard',
            'Phase 1 uses mock data; real AGUI run-events wiring comes later',
        ],
    },
    {
        pattern: '/admin/services/:serviceId/billing',
        section: 'admin',
        view: 'service-billing',
        descriptionTemplate: 'User is viewing billing setup for catalog service {serviceId}',
        capabilities: [
            'Compose one-time, recurring, pay-as-you-go, included-usage, and tiered prices',
            'Draft several prices and explicitly save or cancel the blueprint changes',
        ],
        hints: [
            SERVICES_SESSION_MOCK_HINT,
            'Blueprint price changes apply to future organization assignments, not existing ones',
        ],
    },
    {
        pattern: '/admin/services/:serviceId/playbooks',
        section: 'admin',
        view: 'service-playbooks',
        descriptionTemplate: 'User is viewing playbooks attached to catalog service {serviceId}',
        capabilities: ['Attach or remove fulfillment playbooks for this service'],
        hints: [SERVICES_SESSION_MOCK_HINT],
    },
    {
        pattern: '/admin/services/:serviceId',
        section: 'admin',
        view: 'service-detail',
        descriptionTemplate: 'User is viewing catalog service {serviceId}',
        capabilities: [
            'Read the service header: name, description, availability, and organization usage',
            'Edit, archive, or restore the service blueprint',
        ],
        hints: [SERVICES_SESSION_MOCK_HINT],
    },
    {
        pattern: '/admin/services',
        section: 'admin',
        view: 'services',
        descriptionTemplate: 'User is viewing the admin services catalog page',
        capabilities: [
            'Browse and search the catalog of sold services in a resizable sidebar',
            'Create a new service from the topbar',
            'Select a service to open its detail pane',
        ],
        hints: ['Services is under Business in the admin sidebar', SERVICES_SESSION_MOCK_HINT],
    },
    {
        pattern: '/admin/team',
        section: 'admin',
        view: 'team',
        descriptionTemplate: 'User is viewing the admin team roster',
        capabilities: [
            'Browse internal team members in a flat DataTable',
            'Search members by name, role, or email',
        ],
        hints: [
            'Team is under Operations in the admin sidebar',
            'Roster comes from GET /admin/team/members (Firebase Auth admins)',
        ],
    },
    {
        pattern: '/admin',
        section: 'admin',
        view: 'admin-root',
        descriptionTemplate: 'User is in the admin shell',
        capabilities: ['Navigate admin sections'],
        hints: ['Admin chat talks to platform-tenancy agents only'],
    },
];
